import { useState } from 'react';
import { TokenizedInput } from '@/components/ui/TokenizedInput/TokenizedInput';
import { deserialize } from '@/components/ui/TokenizedInput/serialization';
import { TokenDefinition } from '@/components/ui/TokenizedInput/types';
import Json from '@/components/ui/Json';
import {
  User,
  Tag,
  Hash,
  Calendar,
  Search,
  Filter,
  Settings,
  RotateCcw,
  Database,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const SAMPLE_QUERIES = {
  complex:
    'assignee is: "jane" priority is: "high" status is_not: "closed" task management',
  simple: 'assignee is: "john" status is: "open"',
  withDate: 'created gte: "2024-01-01" assignee is: "mike"',
  default:
    'assignee is: "john" status is_not: "closed" created gt: "2024-01-01" additional text here',
};

const tokenDefinitions: TokenDefinition[] = [
  {
    key: 'assignee',
    label: 'Assignee',
    type: 'select',
    description: 'Person assigned to the task',
    icon: User,
    placeholder: 'Select assignee...',
    options: [
      { value: 'john', label: 'John Doe' },
      { value: 'jane', label: 'Jane Smith' },
      { value: 'mike', label: 'Mike Johnson' },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    type: 'select',
    description: 'Current task status',
    icon: Tag,
    placeholder: 'Select status...',
    options: [
      { value: 'open', label: 'Open' },
      { value: 'closed', label: 'Closed' },
      { value: 'in_progress', label: 'In Progress' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    type: 'select',
    description: 'Task priority level',
    icon: Hash,
    placeholder: 'Select priority...',
    options: [
      { value: 'low', label: 'Low Priority' },
      { value: 'medium', label: 'Medium Priority' },
      { value: 'high', label: 'High Priority' },
    ],
    getOptionIcon: (option) => {
      return () => (
        <div
          className={`h-3 w-3 rounded-full ${
            option.value === 'high'
              ? 'bg-red-400'
              : option.value === 'medium'
                ? 'bg-yellow-400'
                : 'bg-green-400'
            }`}
        />
      );
    },
    renderItem: (item) => (
      <div className="flex items-center gap-2">
        <div
          className={`h-2 w-2 rounded-full ${
            item.value === 'high'
              ? 'bg-red-400'
              : item.value === 'medium'
                ? 'bg-yellow-400'
                : 'bg-green-400'
            }`}
        />
        <div className="flex flex-col">
          <span className="font-medium">{item.label}</span>
          <span className="text-muted-foreground text-xs">
            Priority: {item.value}
          </span>
        </div>
      </div>
    ),
  },
  {
    key: 'created',
    label: 'Created Date',
    type: 'date',
    description: 'When the task was created',
    icon: Calendar,
    placeholder: 'Select date...',
  },
  {
    key: 'title',
    label: 'Title',
    type: 'text',
    description: 'Task title or description',
    icon: Tag,
    placeholder: 'Enter title...',
  },
];

interface DemoControlsProps {
  onLoadSample: (query: string) => void;
  onClear: () => void;
}

function DemoControls({ onLoadSample, onClear }: DemoControlsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Demo Controls</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Database className="mr-2 h-4 w-4" />
                Load Sample Data
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => onLoadSample(SAMPLE_QUERIES.simple)}
              >
                Simple Query
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onLoadSample(SAMPLE_QUERIES.complex)}
              >
                Complex Query
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onLoadSample(SAMPLE_QUERIES.withDate)}
              >
                Query with Date
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onLoadSample(SAMPLE_QUERIES.default)}
              >
                Default Query
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="outline" size="sm" onClick={onClear}>
            <RotateCcw className="mr-2 h-4 w-4" />
            Clear All
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface DebugInfoProps {
  value: string;
  parsed: ReturnType<typeof deserialize>;
}

function DebugInfo({ value, parsed }: DebugInfoProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Debug Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <h4 className="mb-2 text-sm font-medium">Raw Value:</h4>
          <code className="text-muted-foreground bg-muted block rounded p-2 text-xs break-all">
            {value || '(empty)'}
          </code>
        </div>

        <Separator />

        <div>
          <h4 className="mb-2 text-sm font-medium">Parsed Structure:</h4>
          <Json>{parsed}</Json>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomActionButtons() {
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        title="Search"
        onClick={() => alert('Search clicked!')}
      >
        <Search className="h-3.5 w-3.5" />
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="h-6 w-6"
        title="Filter"
        onClick={() => alert('Filter clicked!')}
      >
        <Filter className="h-3.5 w-3.5" />
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            title="Settings"
          >
            <Settings className="h-3.5 w-3.5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => alert('Export clicked!')}>
            Export Results
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => alert('Save Query clicked!')}>
            Save Query
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => alert('Advanced Search clicked!')}>
            Advanced Search
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => alert('Settings clicked!')}>
            Settings
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

export function TokenizedInputDemo() {
  const [value, setValue] = useState(SAMPLE_QUERIES.default);

  const handleChange = (newValue: string) => {
    setValue(newValue);
  };

  const handleClear = () => {
    setValue('');
  };

  const handleLoadSample = (query: string) => {
    setValue(query);
  };

  const parsed = deserialize(value);

  return (
    <div className="container mx-auto max-w-6xl space-y-6 p-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold">Tokenized Input Demo</h1>
        <p className="text-muted-foreground">
          Interactive demonstration of the tokenized input component with arrow
          navigation, backspace deletion, autocomplete, and custom action
          buttons.
        </p>
      </div>

      {/* Main Demo Section */}
      <Card>
        <CardHeader>
          <CardTitle>Interactive Demo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">
              Tokenized Search Input
            </label>
            <TokenizedInput
              value={value}
              tokenDefinitions={tokenDefinitions}
              placeholder="Type to add tokens or search..."
              onChange={handleChange}
              onClear={handleClear}
              renderButtons={() => <CustomActionButtons />}
            />
          </div>
        </CardContent>
      </Card>

      {/* Controls and Debug in a grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div>
          <DemoControls onLoadSample={handleLoadSample} onClear={handleClear} />
        </div>

        <div>
          <DebugInfo value={value} parsed={parsed} />
        </div>
      </div>
    </div>
  );
}
